/**
 * POSSEN — Appointment booking
 *
 * Opens Acuity's scheduler inline for the session a visitor chooses, with what
 * we already know passed as Acuity dynamic-link parameters. All copy is
 * rendered by sections/booking.liquid; this file only switches between what is
 * already on the page and builds the scheduler URL, so nothing here needs
 * translating.
 *
 * Acuity's embed.js resizes the iframe to its content and keeps the parent page
 * scrolled to the right step. It registers iframes that exist when it runs, so
 * it is loaded again after every iframe this file creates; it skips frames it
 * has already seen.
 */
(function () {
  'use strict';

  var EMBED_SCRIPT = 'https://embed.acuityscheduling.com/js/embed.js';
  var MAX_TITLE = 120;
  var MAX_SKU = 64;

  var root = null;
  var config = null;
  var chooser = null;
  var sessions = [];
  var context = null;

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  /* ------------------------------------------------------------------ setup */

  function init() {
    root = document.querySelector('[data-booking]');
    if (!root) return;

    sessions = Array.prototype.slice.call(root.querySelectorAll('[data-booking-session]'));
    if (!sessions.length) return;

    config = readConfig();
    chooser = root.querySelector('[data-booking-chooser]');
    context = readContext();

    renderContext();
    root.addEventListener('click', onClick);

    /* A shared link opens its session straight away. Nothing animates here:
       the page is arriving, not responding to anything the visitor did. */
    var requested = findSession(param('type'));
    if (requested) open(requested, { instant: true });
  }

  function readConfig() {
    var island = root.querySelector('[data-booking-config]');
    try {
      return JSON.parse(island.textContent);
    } catch (err) {
      return { fields: {}, customer: null, language: '' };
    }
  }

  /* ----------------------------------------------------------------- events */

  function onClick(event) {
    var choose = event.target.closest('[data-booking-choose]');
    if (choose) {
      /* Cards are links to Acuity. Let modified clicks open a new tab. */
      if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;

      var session = findSession(choose.getAttribute('data-booking-choose'));
      if (!session) return;

      event.preventDefault();
      open(session, { instant: false });
      return;
    }

    if (event.target.closest('[data-booking-change]')) {
      event.preventDefault();
      close();
    }
  }

  /* ------------------------------------------------------------------ state */

  function open(session, options) {
    sessions.forEach(function (other) {
      if (other !== session) other.hidden = true;
    });

    if (options.instant) {
      session.setAttribute('data-instant', '');
    } else {
      session.removeAttribute('data-instant');
    }

    if (chooser) chooser.hidden = true;
    session.hidden = false;
    root.setAttribute('data-state', 'session');

    mountFrame(session);
    setTypeParam(session.getAttribute('data-booking-session'));

    if (options.instant) return;

    /* The chooser just collapsed, so whatever the visitor was looking at has
       moved. Only reposition when the session actually starts off-screen, and
       jump rather than glide: the layout already changed under them. */
    var heading = session.querySelector('[data-booking-focus]');
    if (heading) heading.focus({ preventScroll: true });
    if (session.getBoundingClientRect().top < headerOffset()) {
      session.scrollIntoView({ block: 'start' });
    }
  }

  function close() {
    var current = activeSession();

    sessions.forEach(function (session) {
      session.hidden = true;
    });
    if (chooser) chooser.hidden = false;
    root.removeAttribute('data-state');
    setTypeParam('');

    if (!chooser) return;

    /* Return focus to the card that opened this session, so keyboard users
       land where they left off. The frame stays mounted, so choosing the same
       session again keeps whatever they had already filled in. */
    var key = current ? current.getAttribute('data-booking-session') : '';
    var card = key ? chooser.querySelector('[data-booking-choose="' + key + '"]') : null;
    var target = card || chooser.querySelector('[data-booking-choose]');
    if (!target) return;

    target.focus({ preventScroll: true });
    if (chooser.getBoundingClientRect().top < headerOffset()) {
      chooser.scrollIntoView({ block: 'start' });
    }
  }

  function activeSession() {
    for (var i = 0; i < sessions.length; i++) {
      if (!sessions[i].hidden) return sessions[i];
    }
    return null;
  }

  function findSession(key) {
    if (!key) return null;
    for (var i = 0; i < sessions.length; i++) {
      if (sessions[i].getAttribute('data-booking-session') === key) return sessions[i];
    }
    return null;
  }

  /* ------------------------------------------------------------------ frame */

  function mountFrame(session) {
    var holder = session.querySelector('[data-booking-frame]');
    if (!holder || holder.querySelector('iframe')) return;

    var url = schedulerUrl(session.getAttribute('data-type-id'));

    var frame = document.createElement('iframe');
    frame.className = 'Booking__iframe';
    frame.title = holder.getAttribute('data-frame-title') || '';
    frame.setAttribute('width', '100%');
    frame.setAttribute('height', '800');
    frame.setAttribute('frameborder', '0');
    /* embed.js subtracts this when it scrolls the page to a new step, so the
       fixed header never covers the top of the calendar. */
    frame.setAttribute('data-offset-top', String(headerOffset()));
    frame.addEventListener('load', function () {
      holder.setAttribute('data-loaded', '');
    });
    frame.src = url;

    holder.appendChild(frame);

    var fallback = session.querySelector('[data-booking-fallback]');
    if (fallback) fallback.href = url;

    loadEmbedScript();
  }

  function loadEmbedScript() {
    var script = document.createElement('script');
    script.src = EMBED_SCRIPT;
    script.async = true;
    document.body.appendChild(script);
  }

  /**
   * Acuity's documented dynamic-link parameters. Intake fields are keyed
   * `field:<id>`; any ID left empty in the theme editor is skipped, as is any
   * value we don't have.
   */
  function schedulerUrl(typeId) {
    var parts = [];
    var customer = config.customer;
    var fields = config.fields || {};

    add('appointmentType', typeId);

    if (customer) {
      add('firstName', customer.firstName);
      add('lastName', customer.lastName);
      add('email', customer.email);
      add('phone', customer.phone);
      addField(fields.customerId, customer.id);
    }

    addField(fields.interest, context.interest);
    addField(fields.source, [context.from || 'direct', config.language].filter(Boolean).join(' · '));

    return root.getAttribute('data-scheduler') + '&' + parts.join('&');

    function add(name, value) {
      if (value === null || value === undefined || value === '') return;
      parts.push(name + '=' + encodeURIComponent(String(value)));
    }

    function addField(id, value) {
      if (!/^\d+$/.test(String(id || ''))) return;
      add('field:' + id, value);
    }
  }

  /* ---------------------------------------------------------------- context */

  /**
   * What the visitor was looking at, from the product page link. Everything
   * here comes from the URL, so it is length-capped and only ever written as
   * text, never as markup.
   */
  function readContext() {
    var handle = param('product');
    var title = clip(param('title'), MAX_TITLE);
    var sku = clip(param('sku'), MAX_SKU);
    var from = param('from');

    if (!/^[a-z0-9][a-z0-9-]*$/i.test(handle)) handle = '';
    if (!/^[a-z0-9][a-z0-9-]{0,39}$/i.test(from)) from = '';

    var interest = [];
    if (title) interest.push(title);
    if (sku) interest.push('SKU ' + sku);
    if (handle) interest.push(window.location.origin + '/products/' + handle);

    return {
      title: title,
      from: from.toLowerCase(),
      interest: interest.join(' · ')
    };
  }

  function renderContext() {
    var line = root.querySelector('[data-booking-context]');
    var value = root.querySelector('[data-booking-context-value]');
    if (!line || !value || !context.title) return;

    value.textContent = context.title;
    line.hidden = false;
  }

  /* ---------------------------------------------------------------- helpers */

  function param(name) {
    try {
      return (new URLSearchParams(window.location.search).get(name) || '').trim();
    } catch (err) {
      return '';
    }
  }

  function setTypeParam(key) {
    if (!window.history || typeof window.history.replaceState !== 'function') return;

    var params = new URLSearchParams(window.location.search);
    if (key) {
      params.set('type', key);
    } else {
      params.delete('type');
    }

    var query = params.toString();
    window.history.replaceState(
      window.history.state,
      '',
      window.location.pathname + (query ? '?' + query : '') + window.location.hash
    );
  }

  /* theme.liquid publishes the fixed header's height as --header-height. */
  function headerOffset() {
    var value = window.getComputedStyle(document.documentElement).getPropertyValue('--header-height');
    return parseInt(value, 10) || 0;
  }

  function clip(value, max) {
    value = String(value || '').replace(/\s+/g, ' ').trim();
    return value.length > max ? value.slice(0, max).trim() : value;
  }
})();
